/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { Drawer, IModuleProps, INodeProps, Module } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { AttributesForSelectedVariantInput, getAttributesForSelectedVariant } from '@msdyn365-commerce-modules/product-specification';
import { ProductSpecificationTableRow } from './components/product-specification-table-stucture';
import { IProductSpecificationData } from './product-specification.data';
import { displayStyle, IProductSpecificationProps } from './product-specification.props.autogenerated';
// import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
/**
 * Maintain Drawer State.
 */
export interface IProductSpecificationState {
    isDrawerOpen: boolean;
    isAdditionalMediaDrawerOpen: boolean;
    drawerListId: number | undefined;
}

/**
 * Product specification view props.
 */
export interface IProductSpecificationViewProps extends IProductSpecificationProps<{}> {
    ProductSpecification: IModuleProps;
    ProductSpecificationTableProps: INodeProps;
    ProductSpecificationTableBodyProps: INodeProps;
    productSpecificationResult?: (JSX.Element | null)[];

    title?: React.ReactNode;
    isAccrodion?: boolean;
    handleText?(event: Msdyn365.ContentEditableEvent): void;
}

/**
 * Check for display style either accordion or table.
 */
export interface IProductSpecificationExtentedProps extends IProductSpecificationProps<IProductSpecificationData> {
    displayStyle?: displayStyle;
}

/**
 *
 * ProductSpecification component.
 * @extends {React.Component<IProductSpecificationProps<IProductSpecificationData>>}
 */
@observer
class ProductSpecification extends React.Component<IProductSpecificationExtentedProps, IProductSpecificationState> {
    public constructor(props: IProductSpecificationExtentedProps) {
        super(props);
        this.state = {
            isDrawerOpen: false,
            isAdditionalMediaDrawerOpen: false,
            drawerListId: undefined
        };
    }

    public async componentDidMount(): Promise<void> {
        const { context, data, telemetry } = this.props;
        const catalogId = Msdyn365.getCatalogId(this.props.context.request);
        validateCatalogId(catalogId);
        reaction(
            () => (data.product.result ? data.product.result.RecordId : null),
            () => {
                if (context && data.product.result) {
                    const actionInput = new AttributesForSelectedVariantInput(
                        data.product.result.RecordId,

                        +context.actionContext.requestContext.apiSettings.channelId,
                        data.product.result,
                        catalogId
                    );

                    getAttributesForSelectedVariant(actionInput, this.props.context.actionContext).catch((error: Error) => {
                        telemetry.error(error.message);
                        telemetry.debug('Unable to update attributes for Product');
                        telemetry.error(error.message);
                    });
                }
            }
        );
    }

    public shouldComponentUpdate(nextProps: IProductSpecificationExtentedProps, nextState: IProductSpecificationState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        let data: IProductSpecificationData;
        try {
            data = this.props.data;
        } catch (error) {
            this.props.telemetry.error(`Something went wrong ------${error}`);
            // @ts-ignore
            return <div>{error}</div>;
        }

        if (!data.productSpecificationData.result || data.productSpecificationData.result.length <= 0) {
            this.props.telemetry.error('Product specification content is empty, module wont render.');
            return null;
        }
        const { heading, className } = this.props.config;
        const { productSpecificationData } = this.props.data;
        const isAccrodion = this.props.config.displayStyle === displayStyle.accordion;
        // @ts-ignore
        const viewProps = {
            ...this.props,
            handleText: this.handleHeadingChange,
            ProductSpecification: {
                moduleProps: this.props,
                className: classnames('ms-product-specification', className)
            },
            ProductSpecificationTableProps: isAccrodion
                ? {
                      moduleProps: this.props,
                      className: classnames('ms-product-specification__accordion', className)
                  }
                : {
                      className: classnames('ms-product-specification__table table-striped table-bordered'),
                      tag: 'table'
                  },
            isAccrodion,
            ProductSpecificationTableBodyProps: isAccrodion
                ? {}
                : {
                      className: '',
                      tag: 'tbody'
                  },
            title: heading?.text && (
                <Msdyn365.Text
                    className='ms-product-specification__heading'
                    tag={heading.tag ?? 'h2'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            productSpecificationResult:
                productSpecificationData.result && (isAccrodion ? this._createDrawerBody(data) : this._createTableBody(data))
        } as IProductSpecificationViewProps;
        // @ts-ignore
        let description = '';
        if (this.props.data.product.result && this.props.data.product.result['Description']) {
            description = this.props.data.product.result['Description'];
        }
        const spec = this.props.data.productSpecificationData.result ? this.props.data.productSpecificationData.result : [];
        const nutritionSpec = spec.filter(sp => {
            return sp.Name?.toLowerCase() === 'carbs' || sp.Name?.toLowerCase() === 'protein' || sp.Name?.toLowerCase() === 'fat';
        });
        let nutritionValues = [];
        if (nutritionSpec && nutritionSpec.length > 0) {
            nutritionValues = nutritionSpec.filter(value => {
                return Number(value?.TextValue) > 0;
            });
        }
        const blackListedSpecs = [
            'carbs',
            'protein',
            'fat',
            'products',
            'featured',
            'search keywords',
            'image url',
            'product info',
            'shelf life (days)',
            'hideOOS',
            'stock status',
            'sourced region',
            'quick delivery limit',
            'ecom_product_name',
            'ecom_uom'
        ];
        const filteredSpec = spec.filter(sp => {
            return sp.Name && !blackListedSpecs.includes(sp.Name?.toLowerCase()) && sp?.TextValue !== '';
        });
        return (
            <>
                <div className='localy-product-spec'>
                    {filteredSpec.map((key, index) => {
                        if (key.TextValue && index !== 1) {
                            return (
                                <>
                                    <div className='product-spec-title'> {key.Name}</div>
                                    <div className='product-spec-value'> {key.TextValue}</div>
                                </>
                            );
                        } else if (index === 1) {
                            return (
                                <>
                                    <div className='product-spec-title'>{key.Name}</div>
                                    <div className='product-spec-value'>{key.TextValue}</div>
                                    {/* <div className='product-spec-title'>Product Info</div>
                                    <div className='product-spec-value'>{description}</div> */}
                                    {nutritionSpec && nutritionSpec.length > 0 && nutritionValues.length > 0 && (
                                        <>
                                            <div className='product-spec-title'>Nutrition</div>
                                            <div className='product-spec-value'>
                                                <div className='progressBar'>
                                                    {nutritionSpec.map((nutrition, index) => {
                                                        // const rotationValue = Number(nutrition?.TextValue) / 100;
                                                        return (
                                                            <>
                                                                <div className='product-spec-value nutrition-spec-text'>
                                                                    {nutrition.Name}{' '}
                                                                    <span className='nutrition-value'>
                                                                        {Number(nutrition.TextValue).toFixed(2)}gms
                                                                    </span>
                                                                </div>
                                                                {/* <CircularProgressbar
                                                                    styles={buildStyles({
                                                                        // Rotation of path and trail, in number of turns (0-1)
                                                                        rotation: rotationValue,

                                                                        // Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
                                                                        strokeLinecap: 'butt',

                                                                        // Text size
                                                                        textSize: '33px',

                                                                        // How long animation takes to go from one percentage to another, in seconds
                                                                        pathTransitionDuration: 0.5,

                                                                        // Can specify path transition in more detail, or remove it entirely
                                                                        // pathTransition: 'none',
                                                                        // Colors
                                                                        pathColor: `rgb(97, 15, 209, 100)`,
                                                                        textColor: '#131214',
                                                                        trailColor: '#F4EFF8',
                                                                        backgroundColor: '#FFFFF'
                                                                    })}
                                                                    value={Number(nutrition?.TextValue)}
                                                                    text={`${Number(nutrition?.TextValue)}%`}
                                                                />
                                                                <div className='product-spec-value'> {nutrition.Name}</div> */}
                                                            </>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </>
                            );
                        } else {
                            return '';
                        }
                    })}
                </div>
            </>
        );
    }

    /**
     * Handle Heading Text Change.
     * @param event - Current event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    private _createTableBody(content: IProductSpecificationData): (JSX.Element | null)[] {
        const tableRows = content.productSpecificationData.result!.map((product, index) => {
            const cellData: JSX.Element | null = this._renderProductCell(product);

            if (!cellData) {
                return null;
            }

            return (
                <>
                    <ProductSpecificationTableRow
                        className='ms-product-specification__table-row'
                        key={index}
                        productName={product.Name}
                        cellData={cellData}
                    />
                </>
            );
        });

        if (this.props.context.request.app?.config?.OmniChannelMedia && this.props.data.additionalMediaLocations.result?.length) {
            tableRows.push(this._createAdditionalMediaDrawer());
        }

        return tableRows;
    }

    private _createDrawerBody(content: IProductSpecificationData): (JSX.Element | null)[] {
        const accordionItemContainer: IModuleProps = {
            moduleProps: this.props,
            className: 'ms-product-specification-title'
        };

        const drawers = content.productSpecificationData.result!.map(product => {
            const cellData: JSX.Element | null = this._renderProductCell(product);
            if (!cellData) {
                return null;
            }
            return (
                <Module {...accordionItemContainer} key={product.RecordId}>
                    <Drawer
                        key={product.RecordId}
                        collapseProps={{
                            timeout: 0,
                            isOpen: this.state.isDrawerOpen && this.state.drawerListId === product.RecordId
                        }}
                        className='ms-product-specification__drawer'
                        openGlyph='ms-product-specification__drawer-open'
                        closeGlyph='ms-product-specification__drawer-close'
                        glyphPlacement='end'
                        toggleButtonText={product.Name ?? ''}
                        onToggle={this._toggle(product.RecordId)}
                    >
                        <div className='ms-product-details'>{cellData}</div>
                    </Drawer>
                </Module>
            );
        });

        if (this.props.context.request.app?.config?.OmniChannelMedia && this.props.data.additionalMediaLocations.result?.length) {
            drawers.push(this._createAdditionalMediaDrawer());
        }

        return drawers;
    }
    private _createAdditionalMediaDrawer(): JSX.Element {
        const accordionItemContainer: IModuleProps = {
            moduleProps: this.props,
            className: 'ms-product-specification-title'
        };

        return (
            <Module {...accordionItemContainer} key={'additional_media_drawer'}>
                <Drawer
                    key={'additional_media_drawer'}
                    collapseProps={{
                        timeout: 0,
                        isOpen: this.state.isAdditionalMediaDrawerOpen
                    }}
                    className='ms-product-specification__drawer'
                    openGlyph='ms-product-specification__drawer-open'
                    closeGlyph='ms-product-specification__drawer-close'
                    glyphPlacement='end'
                    toggleButtonText={this.props.resources.additionalDownloadsText}
                    onToggle={() => {
                        this.setState(prevState => ({
                            ...prevState,
                            isAdditionalMediaDrawerOpen: !prevState.isAdditionalMediaDrawerOpen
                        }));
                    }}
                >
                    <div className='ms-product-details'>
                        <ul className='ms-product-details-additional-media'>
                            {(this.props.data.additionalMediaLocations.result || []).map(mediaLocation => (
                                <li key={mediaLocation.Uri}>
                                    <a href={mediaLocation.Uri} download>
                                        {mediaLocation.AltText}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Drawer>
            </Module>
        );
    }

    private readonly _toggle = (drawerListId: number | undefined) => (): void => {
        if (this.state.drawerListId !== drawerListId) {
            this.setState({ drawerListId, isDrawerOpen: true });
        } else {
            this.setState({ drawerListId: undefined, isDrawerOpen: false });
        }
    };

    private _renderProductCell(product: AttributeValue): JSX.Element | null {
        switch (product.DataTypeValue) {
            case 0: // None
                // Return span here instead of null so the row still shows
                return <span />;
            case 1: // Currency
                return this._renderCurrencyCell(product);
            case 2: // DateTime
                return this._renderDateTimeCell(product);
            case 3: // Decimal
                return this._renderDecimalCell(product);
            case 4: // Integer
                return this._renderIntegerCell(product);
            case 5: // Text
                return this._renderTextCell(product);
            case 6: // TrueFalse
                return this._renderTrueFalseCell(product);
            case 40: // Video
                this.props.telemetry.warning('Got video specification type, not supported yet so skipping line');
                return null;
            case 41: // Image
                this.props.telemetry.warning('Got image specification type, not supported yet so skipping line');
                return null;
            default:
                this.props.telemetry.warning(`Got unknown specification type ${product.DataTypeValue} so skipping line`);
                return null;
        }
    }

    private _renderCurrencyCell(product: AttributeValue): JSX.Element | null {
        if (product.CurrencyValue !== null && product.CurrencyValue !== undefined) {
            return (
                <span>
                    {product.CurrencyCode}
                    {product.CurrencyValue}
                </span>
            );
        }

        return null;
    }

    private _renderDateTimeCell(product: AttributeValue): JSX.Element | null {
        if (product.DateTimeOffsetValue?.toUTCString) {
            return <span>{product.DateTimeOffsetValue.toUTCString()}</span>;
        }

        return null;
    }

    private _renderIntegerCell(product: AttributeValue): JSX.Element | null {
        if (product.IntegerValue !== null && product.IntegerValue !== undefined) {
            return <span>{product.IntegerValue}</span>;
        }

        return null;
    }

    private _renderDecimalCell(product: AttributeValue): JSX.Element | null {
        if (product.FloatValue !== null && product.FloatValue !== undefined) {
            return <span>{product.FloatValue}</span>;
        }

        return null;
    }

    private _renderTextCell(product: AttributeValue): JSX.Element | null {
        if (product.TextValue !== null && product.TextValue !== undefined) {
            // eslint-disable-next-line react/no-danger
            return <span dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(product.TextValue)} />;
        }

        return null;
    }

    private _renderTrueFalseCell(product: AttributeValue): JSX.Element | null {
        // TODO 20369885 setting to default value is needed to work around a platform bug where
        // config string value does not get its default value.
        if (product.BooleanValue) {
            return <span>{this.props.resources.trueValueText ? this.props.resources.trueValueText : 'Yes'}</span>;
        }
        return <span>{this.props.resources.falseValueText ? this.props.resources.falseValueText : 'No'}</span>;
    }
}

export default ProductSpecification;
